import { useState } from 'react';
import { usePermissions } from '@/hooks/usePermissions';
import { useAsyncAction } from '@/hooks/useAsyncAction';
import { useTaskDetail, useTasks } from '@/hooks/data/useTasks';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { ClearFiltersButton, ErrorAlert, PageHeader } from '@/components/PageParts';
import { TasksTable, type TaskRowAction } from '@/components/tasks/TasksTable';
import { TaskDetailModal } from '@/components/tasks/TaskDetailModal';
import { taskStatusMap, taskTypeMap } from '@/config/statusMaps';
import { getTaskActions } from '@/config/resourceRules';
import { pluralize } from '@/utils/format';
import type { Task, TaskStatus, TaskType } from '@/types/task';
import './Tasks.css';

const NO_ACTIONS: Record<TaskRowAction, boolean> = { cancel: false, retry: false };

/**
 * Background tasks page (certificate renewals, syncs, reloads, domain setups)
 */
export function Tasks() {
  const { role } = usePermissions();

  // Filters
  const [typeFilter, setTypeFilter] = useState<TaskType | ''>('');
  const [statusFilter, setStatusFilter] = useState<TaskStatus | ''>('');

  const { tasks, isLoading, error: loadError, reload, cancelTask, retryTask } = useTasks({
    type: typeFilter || undefined,
    status: statusFilter || undefined,
  });

  // Detail modal
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const detail = useTaskDetail(selectedTaskId);
  const [actionError, setActionError] = useState<string | null>(null);

  const taskAction = useAsyncAction(
    async (action: TaskRowAction, taskId: string) => {
      await (action === 'cancel' ? cancelTask(taskId) : retryTask(taskId));
      // Refresh the open detail view
      if (taskId === selectedTaskId) {
        await detail.reload();
      }
    },
    {
      errorMessage: (action) => (action === 'cancel' ? 'Failed to cancel task' : 'Failed to retry task'),
      getKey: (action, taskId) => `${action}:${taskId}`,
      onError: setActionError,
    }
  );

  const handleAction = (action: TaskRowAction, taskId: string) => {
    setActionError(null);
    void taskAction.run(action, taskId);
  };

  const handleClearFilters = () => {
    setTypeFilter('');
    setStatusFilter('');
  };

  const handleRefresh = () => {
    setActionError(null);
    void reload();
  };

  const hasActiveFilters = Boolean(typeFilter || statusFilter);
  const selectedTask = detail.task;

  return (
    <div className="tasks-page">
      <PageHeader
        classPrefix="tasks"
        title="Tasks"
        count={pluralize(tasks.length, 'task')}
        actions={
          <Button variant="secondary" onClick={handleRefresh}>
            Refresh
          </Button>
        }
      />

      {/* Filters */}
      <div className="tasks-filters">
        <Select
          value={typeFilter}
          onChange={setTypeFilter}
          placeholder="All Types"
          options={taskTypeMap.options}
          className="tasks-filter-select"
          aria-label="Filter by type"
        />

        <Select
          value={statusFilter}
          onChange={setStatusFilter}
          placeholder="All Status"
          options={taskStatusMap.options}
          className="tasks-filter-select"
          aria-label="Filter by status"
        />

        {hasActiveFilters && <ClearFiltersButton classPrefix="tasks" onClick={handleClearFilters} />}
      </div>

      <ErrorAlert classPrefix="tasks" message={actionError ?? loadError} onRetry={handleRefresh} />

      <TasksTable
        tasks={tasks}
        isLoading={isLoading}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={handleClearFilters}
        onSelect={(task: Task) => setSelectedTaskId(task.id)}
        getActions={(task) => getTaskActions(role, task)}
        isBusy={(action, taskId) => taskAction.isPending(`${action}:${taskId}`)}
        onAction={(action, task) => handleAction(action, task.id)}
      />

      <TaskDetailModal
        isOpen={selectedTaskId !== null}
        onClose={() => setSelectedTaskId(null)}
        task={selectedTask}
        isLoading={detail.isLoading}
        error={detail.error}
        actions={selectedTask ? getTaskActions(role, selectedTask) : NO_ACTIONS}
        isBusy={(action) => !!selectedTaskId && taskAction.isPending(`${action}:${selectedTaskId}`)}
        onAction={(action) => selectedTaskId && handleAction(action, selectedTaskId)}
      />
    </div>
  );
}
